import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { QuoteBreakdown } from "./QuoteBreakdown";
import { Check, AlertCircle, Info, Truck, Clock, MapPin, Loader2 } from "lucide-react";

const RESULT_ANIMATION = { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 } };

export const QuoteResult = ({ quoteResult, loading, onProceedToPayment }) => {
  const navigate = useNavigate();

  if (!quoteResult?.survey_required) {
    return (
      <motion.div key="step6" {...RESULT_ANIMATION} className="space-y-6">
        {/* Instant Estimate Result */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-[#16A34A]/10 mx-auto flex items-center justify-center mb-4">
            <Check className="w-10 h-10 text-[#16A34A]" />
          </div>
          <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A]">Your Estimated Quote</h2>
          <p className="text-slate-600 mt-2">Based on the details you provided</p>
        </div>

        {/* Estimate Badge */}
        <div className="bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Important:</strong> This is an <strong>estimated price</strong> based on the information provided. 
            Final price may vary based on actual conditions on moving day. Price is not binding until confirmed by our team.
          </div>
        </div>

        {/* Move Summary */}
        <div className="bg-slate-50 p-4 grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-[#FF5A1F]" />
            <span className="text-sm">{quoteResult?.vehicle_type}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#FF5A1F]" />
            <span className="text-sm">~{quoteResult?.labour_hours} hours</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#FF5A1F]" />
            <span className="text-sm">{quoteResult?.distance_miles} miles ({quoteResult?.distance_band})</span>
          </div>
        </div>

        <QuoteBreakdown 
          breakdown={quoteResult?.breakdown}
          customerPrice={quoteResult?.customer_price || quoteResult?.estimated_price}
          depositAmount={quoteResult?.deposit_amount}
          surveyRequired={false}
        />

        <Button 
          onClick={onProceedToPayment} 
          className="w-full btn-primary h-14 text-lg"
          disabled={loading}
          data-testid="proceed-to-payment-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Pay Deposit & Book"}
        </Button>

        <p className="text-center text-xs text-slate-500">
          By proceeding, you acknowledge this is an estimate subject to final confirmation.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div key="step6" {...RESULT_ANIMATION} className="space-y-6">
      {/* Survey Required Result */}
      <div className="text-center mb-6">
        <div className="w-20 h-20 bg-[#EAB308]/10 mx-auto flex items-center justify-center mb-4">
          <AlertCircle className="w-10 h-10 text-[#EAB308]" />
        </div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A]">Survey Required</h2>
        <p className="text-slate-600 mt-2">Your move requires a free survey for an accurate quote</p>
      </div>

      {/* Survey Reasons */}
      <div className="bg-amber-50 border border-amber-200 p-4">
        <h3 className="font-semibold text-amber-800 mb-2">Why a survey is needed:</h3>
        <ul className="space-y-2">
          {quoteResult?.survey_reasons?.map((reason) => (
            <li key={reason} className="flex items-start gap-2 text-sm text-amber-700">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Indicative Quote Breakdown */}
      <div className="opacity-75">
        <div className="text-center mb-2">
          <span className="text-xs uppercase tracking-wide text-slate-500">Indicative Estimate Only</span>
        </div>
        <QuoteBreakdown 
          breakdown={quoteResult?.breakdown}
          customerPrice={quoteResult?.customer_price || quoteResult?.estimated_price}
          depositAmount={quoteResult?.deposit_amount}
          surveyRequired={true}
        />
      </div>

      <div className="bg-[#0F172A] p-4 text-center">
        <p className="text-white text-sm">
          Final quote will be provided after our surveyor assesses your property
        </p>
      </div>

      <Button 
        onClick={() => navigate(`/book-survey?quote=${quoteResult?.quote_id}`)}
        className="w-full btn-primary h-14 text-lg"
        data-testid="book-survey-btn"
      >
        Book Free Survey
      </Button>

      <p className="text-center text-sm text-slate-500">
        We've saved your quote. Our team will also be in touch shortly.
      </p>
    </motion.div>
  );
};
